import { useCallback, useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	EDGE_REGION_HINT_HEADER,
	getEdgeRegionHint,
} from '@atlassian/search-common/get-edge-region-hint';
import { SEARCH_USE_CASE_IDS } from '@atlassian/search-common/search-use-case-id';

import { SEARCH_PAGE } from '../../../common/constants/experience';
import { type PrimaryProductKey } from '../../../common/constants/product-keys';
import {
	type SearchPreloadData,
	type SearchPreloadTimings,
} from '../../../common/types/search-preload';
import { getSessionIdFromLocalStorage } from '../../../common/utils/events/get-session-id-from-local-storage';
import { triggerQueryBootstrapConfigSnapshotExperimentExposure } from '../../../common/utils/experiments';
import { type QueryParams } from '../../../common/utils/query-params/types';
import { generateSearchInputs } from '../../../services/search-query-generator';
import {
	RIGHT_PANEL_ASYNC_QUERY_OPERATION_NAME,
	RIGHT_PANEL_ASYNC_QUERY_PERSISTED_ID,
} from '../../../ui/search-page-relay/right-panel/__generated__/rightPanelAsyncQueryMetadata';
import {
	SEARCH_RESULTS_ASYNC_QUERY_OPERATION_NAME,
	SEARCH_RESULTS_ASYNC_QUERY_PERSISTED_ID,
} from '../../../ui/search-page-relay/search-results/__generated__/searchResultsAsyncQueryMetadata';
import {
	createQueryBootstrapSnapshotIdentity,
	prepareQueryBootstrapSnapshotForTransition,
} from '../../config/query-bootstrap-snapshot-store';
import { useLogException } from '../../error-boundary/utils';
import { buildIntentDetectionVariables } from '../../query-intent/build-intent-variables';
import {
	useAggAbsoluteUrl,
	useAvailableProducts,
	useBootstrap,
	useCloudId,
	useExperimentLayers,
	useFeatures,
	useIsJiraIssueIndexed,
	usePrimaryProduct,
	useProductDataRegions,
	useProductEdition,
	useThirdPartyConfigs,
	useUnitMetadata,
	useUser,
} from '../../store/bootstrap';
import { preloadIntentDetection } from './preload-intent-detection';
import { preloadQuery } from './preload-query';

/**
 * Snapshot of all bootstrap-derived dependencies needed to call
 * {@link generateSearchInputs} from outside React's render phase.
 *
 * Bundled onto a ref so the warm-up callback (memoized once) can read the
 * latest values at invocation time without re-creating the callback identity.
 */
type WarmUpDeps = {
	aggAbsoluteUrl: string | undefined;
	cloudId: string | undefined;
	experimentLayers: string[];
	isLiteMode: boolean;
	isJiraIssueIndexed: boolean;
	logError: (error: Error) => Promise<void>;
	primaryProduct: PrimaryProductKey | undefined;
	productDataRegions: ReturnType<typeof useProductDataRegions>[0];
	productEdition: ReturnType<typeof useProductEdition>[0];
	products: ReturnType<typeof useAvailableProducts>;
	thirdPartyConfigs: ReturnType<typeof useThirdPartyConfigs>[0];
	unitMetadata: ReturnType<typeof useUnitMetadata>;
	user: ReturnType<typeof useUser>[0];
	// SAIN intent-detection preload deps.
	activationId: string | undefined;
	orgId: string | undefined;
	locale: string;
};

const DEFAULT_GRAPHQL_ENDPOINT = '/gateway/api/graphql';

/**
 * A small fraction of WebKit-based browsers (desktop Safari, iOS Safari/Chrome/Edge)
 * report `Event.timeStamp` as wall-clock epoch time rather than a
 * `performance.now()`-relative `DOMHighResTimeStamp`, in violation of the spec —
 * confirmed in production by `preloadNavigateAt` values landing near `Date.now()`
 * (~10^12) instead of a small offset. A genuine timestamp for an event that
 * already fired can never be later than `performance.now()` right now, so
 * anything past that bound is discarded here rather than reported as a
 * corrupted UFO mark downstream.
 */
function isPlausiblePerformanceTimestamp(value: number | undefined): value is number {
	return (
		typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= performance.now()
	);
}

/**
 * Hook returning a stable `(url) => void` that — when invoked — kicks off the
 * persisted-query fetches the search page would otherwise issue on mount.
 *
 * Design notes:
 * - The hook subscribes to bootstrap state via React hooks but exposes a
 *   non-reactive callback. All bootstrap deps are mirrored on a ref so the
 *   callback always reads the latest values at invocation time without
 *   destabilising its own identity.
 * - The callback is fire-and-forget — it does not block navigation.
 * - Network and assembly errors are swallowed; warm-up failure is silent and
 *   the search page falls back to its normal mount-time fetch.
 */
export const useWarmUpSearchPageQuery = (): ((url: string, navigateAt?: number) => void) => {
	const [user] = useUser();
	const [primaryProduct] = usePrimaryProduct();
	const [cloudId] = useCloudId();
	const [aggAbsoluteUrl] = useAggAbsoluteUrl();
	const [thirdPartyConfigs] = useThirdPartyConfigs();
	const unitMetadata = useUnitMetadata();
	const [productDataRegions] = useProductDataRegions();
	const [isJiraIssueIndexed] = useIsJiraIssueIndexed();
	const [experimentLayers] = useExperimentLayers();
	const [features] = useFeatures();
	const products = useAvailableProducts();
	const [productEdition] = useProductEdition();
	const logError = useLogException();
	// SAIN intent-detection preload deps (only consumed when the FG is on at
	// invocation time).
	const [{ analyticsAttributes, orgId }] = useBootstrap();
	const { locale } = useIntl();

	const depsRef = useRef<WarmUpDeps>({
		aggAbsoluteUrl,
		cloudId,
		experimentLayers,
		isLiteMode: features?.lite?.enabled ?? false,
		isJiraIssueIndexed,
		logError,
		primaryProduct,
		productDataRegions,
		productEdition,
		products,
		thirdPartyConfigs,
		unitMetadata,
		user,
		activationId: analyticsAttributes?.activationId,
		orgId,
		locale,
	});

	useEffect(() => {
		depsRef.current = {
			aggAbsoluteUrl,
			cloudId,
			experimentLayers,
			isLiteMode: features?.lite?.enabled ?? false,
			isJiraIssueIndexed,
			logError,
			primaryProduct,
			productDataRegions,
			productEdition,
			products,
			thirdPartyConfigs,
			unitMetadata,
			user,
			activationId: analyticsAttributes?.activationId,
			orgId,
			locale,
		};
	});

	return useCallback((url: string, navigateAt?: number) => {
		const deps = depsRef.current;
		if (triggerQueryBootstrapConfigSnapshotExperimentExposure()) {
			const identity = createQueryBootstrapSnapshotIdentity({
				cloudId: deps.cloudId,
			});
			if (identity) {
				prepareQueryBootstrapSnapshotForTransition(identity);
			}
		}

		// Required pieces — bail out silently if bootstrap hasn't populated them yet.
		if (!deps.primaryProduct || !deps.cloudId) {
			return;
		}

		const searchSessionId = crypto.randomUUID();
		const sessionId = getSessionIdFromLocalStorage();

		// Parse query params from the destination URL — the URL is the source
		// of truth for what the search page will render.
		let queryParams: QueryParams = {};
		try {
			const parsed = new URL(url, window.location.origin);
			queryParams = Object.fromEntries(parsed.searchParams) as QueryParams;
		} catch {
			// Malformed URL — abort warm-up.
			return;
		}

		let searchInputs;
		try {
			searchInputs = generateSearchInputs({
				backendExperimentOverrides: [],
				cloudId: deps.cloudId,
				experimentLayers: deps.experimentLayers,
				experience: SEARCH_PAGE,
				useCaseId: SEARCH_USE_CASE_IDS.SEARCH_PAGE,
				isJiraIssueIndexed: deps.isJiraIssueIndexed,
				jsmIncidentFilterIds: [],
				primaryProduct: deps.primaryProduct,
				productDataRegions: deps.productDataRegions || {},
				productEdition: deps.productEdition || {},
				products: deps.products,
				queryParams,
				searchSessionId,
				sessionId,
				thirdPartyConfigs: deps.thirdPartyConfigs,
				unitMetadata: deps.unitMetadata,
				user: deps.user,
				expectResult: false,
				onError: deps.logError,
				// Forward lite mode so the prewarmed searchResults query matches the
				// variant the search page fires on mount. Omitting it defaults to
				// `false`, which drifts `includeJiraLiteModeFields` from the SPA's
				// `true` and silently misses the persisted-query cache on the lite
				// (test) cohort — inflating the searchResultsAsyncQuery network fetch.
				isLiteMode: deps.isLiteMode,
			});
		} catch {
			// generateSearchInputs threw — abort warm-up silently.
			return;
		}

		const edgeRegionHint =
			deps.primaryProduct && deps.productDataRegions
				? getEdgeRegionHint(deps.primaryProduct, deps.productDataRegions)
				: undefined;

		const searchResultsQueryVariables = {
			...searchInputs.searchResultsQueryVariables,
			...searchInputs.searchResultsQueryRelayProvidedVariables,
		};

		// Stamped immediately before the fetch is issued, so the search page can
		// measure how long the user waited before the warm-up left the browser.
		const timings: SearchPreloadTimings = {
			navigateAt: isPlausiblePerformanceTimestamp(navigateAt) ? navigateAt : undefined,
			preloadStartAt: performance.now(),
		};

		// Fire-and-forget — both fetches.
		void preloadQuery({
			persistedId: SEARCH_RESULTS_ASYNC_QUERY_PERSISTED_ID,
			operationName: SEARCH_RESULTS_ASYNC_QUERY_OPERATION_NAME,
			queryVariables: searchResultsQueryVariables,
			edgeRegionHint,
			aggAbsoluteUrl: deps.aggAbsoluteUrl || DEFAULT_GRAPHQL_ENDPOINT,
		});
		// Multi-site counts are unavailable, and lite mode does not render the right panel.
		const skipMultiSiteCounts = searchInputs.rightPanelQueryVariables.cloudIdARIs.length > 1;
		if (!deps.isLiteMode && !skipMultiSiteCounts) {
			void preloadQuery({
				persistedId: RIGHT_PANEL_ASYNC_QUERY_PERSISTED_ID,
				operationName: RIGHT_PANEL_ASYNC_QUERY_OPERATION_NAME,
				queryVariables: searchInputs.rightPanelQueryVariables,
				edgeRegionHint,
				aggAbsoluteUrl: deps.aggAbsoluteUrl || DEFAULT_GRAPHQL_ENDPOINT,
			});
		}

		// SAIN intent-detection preload
		// Fires `getQueryIntent` early from the warm-up, supported by the request cache inside search-ai
		const rawSearchText = queryParams.text;
		const searchTextFromUrl = typeof rawSearchText === 'string' ? rawSearchText : '';
		if (
			searchTextFromUrl &&
			// Skip preload when `kd_recent_activity_intent` is on. The FG is being unlaunched (false path kept).
			!fg('kd_recent_activity_intent') &&
			fg('rovo_fps_preload_sain_intent_from_quick_find')
		) {
			void preloadIntentDetection({
				...buildIntentDetectionVariables({
					cloudId: deps.cloudId,
					primaryProduct: deps.primaryProduct,
					activationId: deps.activationId,
					locale: deps.locale,
					orgId: deps.orgId,
					query: searchTextFromUrl,
				}),
				onError: deps.logError,
			});
		}

		const preloadData: SearchPreloadData = {
			cloudId: deps.cloudId,
			config: null,
			isConfigFromCache: undefined,
			configFailReason: undefined,
			cacheFailReason: undefined,
			search: {
				queryVariables: searchResultsQueryVariables,
				generated: { sessionId, searchSessionId },
				steeringHeader: edgeRegionHint
					? `${EDGE_REGION_HINT_HEADER}: ${edgeRegionHint}`
					: undefined,
				timings,
			},
			rightPanel: {
				queryVariables: searchInputs.rightPanelQueryVariables,
				generated: { sessionId, searchSessionId },
				steeringHeader: edgeRegionHint
					? `${EDGE_REGION_HINT_HEADER}: ${edgeRegionHint}`
					: undefined,
			},
			source: 'quick-find',
		};

		try {
			(window as unknown as { __PRELOADED_SEARCH__?: SearchPreloadData }).__PRELOADED_SEARCH__ =
				preloadData;
		} catch {
			// window may be unavailable in unusual environments.
		}
	}, []);
};
